import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTeachersDirect,
  createUnavailability,
  getUnavailability,
  deleteUnavailability,
} from '@timetable/api';
import type {
  CreateUnavailabilityRequest,
  UnavailabilityWithRelations,
} from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '../Toast';
import { queryKeys } from '../../lib/queryKeys';
import { EmptyState } from '../EmptyState';

/**
 * UnavailabilityTab component
 * 
 * Dedicated tab on the Absences page for managing teacher unavailability records.
 * Allows admins to mark teachers as unavailable on specific dates or date ranges,
 * preventing them from being assigned substitution duties.
 * 
 * Requirements: 9.1–9.6
 */
export function UnavailabilityTab() {
  const queryClient = useQueryClient();
  const { schoolContext, user } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();

  // Form state
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [reason, setReason] = useState('');

  // UI state
  const [error, setError] = useState<string | null>(null);

  // Fetch teachers for the dropdown (Req 9.2)
  const { data: teachers, isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || undefined }),
    enabled: !!schoolContext,
  });

  // Fetch unavailability records for the list (Req 9.6)
  const {
    data: unavailabilityRecords,
    isLoading: loadingRecords,
    error: fetchError,
  } = useQuery({
    queryKey: queryKeys.unavailability.list({ school_id: schoolContext || '' }),
    queryFn: () =>
      getUnavailability({
        school_id: schoolContext || '',
      }),
    enabled: !!schoolContext,
  });

  // Create unavailability mutation (Req 9.2, 9.4)
  const createMutation = useMutation({
    mutationFn: (req: CreateUnavailabilityRequest) => createUnavailability(req),
    onSuccess: () => {
      // Invalidate queries to refresh the list (Req 9.5)
      queryClient.invalidateQueries({ queryKey: queryKeys.unavailability.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });

      showSuccess('Unavailability record created successfully');

      // Reset form to defaults
      setSelectedTeacherId('');
      setStartDate(new Date());
      setEndDate(new Date());
      setReason('');
      setError(null);
    },
    onError: (err: any) => {
      // Surface overlap validation errors inline (Req 9.4)
      const conflictList = err?.conflicts || err?.data;
      if (Array.isArray(conflictList) && conflictList.length > 0) {
        const conflicts = conflictList as Array<{ start_date: string; end_date: string }>;
        const conflictDates = conflicts
          .map((c) => `${c.start_date} – ${c.end_date}`)
          .join(', ');
        setError(
          `This teacher already has an unavailability record overlapping this date range: ${conflictDates}`
        );
      } else {
        setError(err.message || 'Failed to create unavailability record');
      }
    },
  });

  // Delete unavailability mutation (Req 9.5)
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUnavailability(id),
    onSuccess: () => {
      // Immediate cache invalidation (Req 9.5)
      queryClient.invalidateQueries({ queryKey: queryKeys.unavailability.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });

      showSuccess('Unavailability record deleted');
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to delete unavailability record');
    },
  });

  // Handle start date change — auto-advance end date if it falls behind
  const handleStartDateChange = (newStart: Date | null) => {
    setStartDate(newStart);
    if (newStart && (!endDate || endDate < newStart)) {
      setEndDate(newStart);
    }
  };

  // Handle form submission (Req 9.2)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    if (!endDate) {
      setError('Please select an end date');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be on or after start date');
      return;
    }

    if (!selectedTeacherId) {
      setError('Please select a teacher');
      return;
    }

    const schoolId = schoolContext;
    if (!schoolId) {
      setError('School context not found. Please log in again.');
      return;
    }

    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];

    const unavailabilityData: CreateUnavailabilityRequest = {
      school_id: schoolId,
      teacher_id: selectedTeacherId,
      start_date: startDateStr,
      end_date: endDateStr,
      reason: reason || undefined,
      created_by: user?.id || undefined,
    };

    createMutation.mutate(unavailabilityData);
  };

  // Handle delete with confirmation
  const handleDelete = (id: string, teacherName: string, dateRange: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete the unavailability record for ${teacherName} (${dateRange})?`
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  // Format date range for display (Req 9.6)
  const formatDateRange = (record: UnavailabilityWithRelations): string => {
    const start = format(parseISO(record.start_date), 'MMM d, yyyy');
    if (record.start_date === record.end_date) return start;
    const end = format(parseISO(record.end_date), 'MMM d, yyyy');
    return `${start} – ${end}`;
  };

  const isSubmitting = createMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Form Section (Req 9.2) */}
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Mark Teacher Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Teachers marked as unavailable will be excluded from substitution assignments on the
            specified dates.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {/* Inline error (includes overlap validation — Req 9.4) */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Teacher Selector (Req 9.2) */}
            <TextField
              select
              label="Teacher"
              value={selectedTeacherId}
              onChange={(e) => setSelectedTeacherId(e.target.value)}
              disabled={loadingTeachers || isSubmitting}
              required
              fullWidth
            >
              {teachers?.map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Start Date Picker (Req 9.2) */}
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              disabled={isSubmitting}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
              }}
            />

            {/* End Date Picker (Req 9.2) */}
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              disabled={isSubmitting}
              minDate={startDate || undefined}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  helperText: 'Defaults to start date for single-day unavailability',
                },
              }}
            />

            {/* Reason (optional) (Req 9.2) */}
            <TextField
              label="Reason (Optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isSubmitting}
              multiline
              rows={2}
              fullWidth
              placeholder="e.g., Off-site training, Personal leave"
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || loadingTeachers}
              fullWidth
              size="large"
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Creating Record…
                </>
              ) : (
                'Mark Unavailable'
              )}
            </Button>
          </Box>
        </Paper>

        {/* List Section (Req 9.6) */}
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Unavailability Records
          </Typography>

          {loadingRecords ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : fetchError ? (
            <Alert severity="error">
              Failed to load unavailability records: {(fetchError as Error).message}
            </Alert>
          ) : !unavailabilityRecords || unavailabilityRecords.length === 0 ? (
            <EmptyState
              title="No unavailability records"
              description="Teachers marked as unavailable will appear here."
            />
          ) : (
            <List disablePadding>
              {unavailabilityRecords.map((record, index) => {
                const dateRange = formatDateRange(record);
                const teacherName = record.teacher?.name || 'Unknown Teacher';

                return (
                  <Box key={record.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      disablePadding
                      sx={{ py: 1.5 }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDelete(record.id, teacherName, dateRange)}
                          disabled={isDeleting}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight="medium">
                            {teacherName}
                          </Typography>
                        }
                        secondary={
                          <Box
                            component="span"
                            sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
                          >
                            <Typography variant="body2" component="span" color="text.secondary">
                              {dateRange}
                            </Typography>
                            {record.reason && (
                              <Typography variant="caption" component="span" color="text.secondary">
                                Reason: {record.reason}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </Box>
                );
              })}
            </List>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}
